import React, { useState, useRef, useEffect } from 'react';
import { Avatar, Button, message } from 'antd';
import { UserOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';

// eslint-disable-next-line import/no-cycle
import { LabeledCard } from '..';
import { SendSvg } from '../Icon/svgIcons';
import { callAgent, createAgentSession } from '../../../services/agent';
import { getUsername, getFullname, getAccessToken } from '../../../services/authentication';
import { parseJwt } from '../../../services/transports';
import { sopViewProfile } from '../../../api/stakeholderUsers';
import robotIconBlack from '../../../images/robotIconBlack.png';
import robotIconWhite from '../../../images/robotIconWhite.png';

import style from './Chatbot.module.scss';

// Constants
const SESSION_STORAGE_KEY_PREFIX = 'sanad_session_id_';
const SESSION_TIMESTAMP_KEY_PREFIX = 'sanad_session_timestamp_';
const MESSAGES_STORAGE_KEY_PREFIX = 'sanad_chat_messages_';
const SESSION_DURATION = 24 * 60 * 60 * 1000; // 24 hours in milliseconds
const MAX_MESSAGE_LENGTH = 400;

// Get user-specific storage keys
const getStorageKeys = () => {
  const username = getUsername() || 'anonymous';

  return {
    sessionId: `${SESSION_STORAGE_KEY_PREFIX}${username}`,
    timestamp: `${SESSION_TIMESTAMP_KEY_PREFIX}${username}`,
    messages: `${MESSAGES_STORAGE_KEY_PREFIX}${username}`
  };
};

// Common questions - using translations
const getCommonQuestions = t => [
  {
    id: 1,
    text: t('chatbot.commonQuestions.question1')
  },
  {
    id: 2,
    text: t('chatbot.commonQuestions.question2')
  },
  {
    id: 3,
    text: t('chatbot.commonQuestions.question3')
  }
];


export const Chatbot = () => {
  const [ messages, setMessages ] = useState([]);
  const [ inputValue, setInputValue ] = useState('');
  const [ isTyping, setIsTyping ] = useState(false);
  const [ sessionId, setSessionId ] = useState(null);
  const [ establishmentName, setEstablishmentName ] = useState({ ar: '', en: '' });
  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);
  const { t, i18n } = useTranslation();

  // Auto-resize textarea without using Ant Design's autoSize
  const adjustTextareaHeight = () => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      const scrollHeight = textareaRef.current.scrollHeight;
      const minHeight = 32;
      const maxHeight = 96;
      const newHeight = Math.min(Math.max(scrollHeight, minHeight), maxHeight);
      textareaRef.current.style.height = `${newHeight}px`;
    }
  };

  useEffect(() => {
    adjustTextareaHeight();
  }, [ inputValue ]);


  // Fetch establishment name from API
  useEffect(() => {
    const fetchEstablishmentName = async() => {
      try {
        const response = await sopViewProfile();
        if (response?.data?.container?.establishmentName) {
          const name = response.data.container.establishmentName;
          setEstablishmentName({ ar: name, en: name });
        }
      } catch (error) {
        // Establishment name is optional, fail silently
      }
    };

    fetchEstablishmentName();
  }, []);

  // Get current user information
  const getCurrentUserInfo = () => {
    try {
      const accessToken = getAccessToken();
      if (!accessToken) {
        return null;
      }

      const tokenData = parseJwt(accessToken);
      const username = getUsername();
      const fullname = getFullname();

      return {
        username: username || tokenData.sub || tokenData.username || tokenData.userName || '',
        name_ar: tokenData.name_ar || tokenData.nameAr || fullname || '',
        name_en: tokenData.name_en || tokenData.nameEn || tokenData.name || fullname || '',
        email: tokenData.email || '',
        establishment_name_ar: establishmentName.ar || '',
        establishment_name_en: establishmentName.en || ''
      };
    } catch (error) {
      return null;
    }
  };

  // Format user info message
  const getUserInfoMessage = () => {
    const userInfo = getCurrentUserInfo();
    if (!userInfo) {
      return t('chatbot.userInfoAccessError');
    }

    const isArabic = i18n.language === 'ar';
    const name = isArabic
      ? (userInfo.name_ar || userInfo.name_en)
      : (userInfo.name_en || userInfo.name_ar);
    const email = userInfo.email;
    const establishmentNameText = isArabic
      ? (userInfo.establishment_name_ar || userInfo.establishment_name_en)
      : (userInfo.establishment_name_en || userInfo.establishment_name_ar);

    const displayName = name || userInfo.username || t('chatbot.userInfoPlaceholders.user');
    const username = userInfo.username || t('chatbot.userInfoPlaceholders.notAvailable');

    let nameLabel = '';
    if (name) {
      nameLabel = isArabic ? `الاسم: ${name}\n` : `Name: ${name}\n`;
    }

    let establishmentLabel = '';
    if (establishmentNameText) {
      establishmentLabel = isArabic ? `اسم المنشأة: ${establishmentNameText}\n` : `Establishment Name: ${establishmentNameText}\n`;
    }

    let emailLabel = '';
    if (email) {
      emailLabel = isArabic ? `البريد الإلكتروني: ${email}` : `Email: ${email}`;
    }

    return t('chatbot.userInfoGreeting', {
      displayName,
      username,
      nameLabel,
      fullName: '',
      establishmentLabel,
      establishment: '',
      emailLabel
    });
  };

  // Check if response indicates agent doesn't know the answer
  const isNoAnswerFound = responseText => {
    if (!responseText) { return false; }
    const lowerText = responseText.toLowerCase();
    const noAnswerPhrases = [
      'لم أجد إجابة',
      'لم أجد',
      'لا توجد إجابة',
      'لا أعرف',
      'لا يمكنني',
      'i did not find',
      'no answer found',
      'i cannot find',
      'i don\'t know',
      'unable to find'
    ];

    return noAnswerPhrases.some(phrase => lowerText.includes(phrase.toLowerCase()));
  };

  // Format message text with bullet points, lists, and clickable links
  const formatMessageText = text => {
    if (!text) { return text; }

    // Function to make URLs clickable
    const makeLinksClickable = (lineText, lineIdx) => {
      const urlRegex = /(https?:\/\/[^\s]+)/g;
      const parts = lineText.split(urlRegex);

      return parts.map((part, partIndex) => {
        if (urlRegex.test(part)) {
          return (
            <a
              key={ `link-${lineIdx}-${partIndex}` }
              href={ part }
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: '#1890ff', textDecoration: 'underline' }}
            >
              {part}
            </a>
          );
        }

        return part;
      });
    };

    // Split by newlines
    const lines = text.split('\n');
    const formattedLines = lines.map((line, lineIndex) => {
      const trimmedLine = line.trim();
      const lineKey = `line-${lineIndex}`;

      // Check for bullet points: "- ", "• ", "▪ ", "▫ "
      if ((/^[-•▪▫]\s/).test(trimmedLine)) {
        const content = trimmedLine.substring(trimmedLine.indexOf(' ') + 1);

        return (
          <div key={ lineKey } style={{ marginLeft: '16px', marginBottom: '4px' }}>
            <span style={{ marginRight: '8px' }}>•</span>
            {makeLinksClickable(content, lineIndex)}
          </div>
        );
      }

      // Check for numbered lists: "1. ", "2. ", etc.
      if ((/^\d+\.\s/).test(trimmedLine)) {
        const match = trimmedLine.match(/^(\d+)\.\s(.+)/);
        if (match) {
          return (
            <div key={ lineKey } style={{ marginLeft: '16px', marginBottom: '4px' }}>
              <span style={{ marginRight: '8px' }}>
                {match[1]}
                .
              </span>
              {makeLinksClickable(match[2], lineIndex)}
            </div>
          );
        }
      }

      // Regular line
      if (trimmedLine) {
        return <div key={ lineKey } style={{ marginBottom: '4px' }}>{makeLinksClickable(trimmedLine, lineIndex)}</div>;
      }

      // Empty line
      return <div key={ lineKey } style={{ marginBottom: '4px' }}>&nbsp;</div>;
    });

    return <div>{formattedLines}</div>;
  };

  // Initialize agent session
  useEffect(() => {
    const initializeSession = async() => {
      try {
        const agentName = process.env.REACT_APP_BAAS_AGENT_NAME || 'sanad';
        const storageKeys = getStorageKeys();

        // Check for existing session
        const storedSessionId = localStorage.getItem(storageKeys.sessionId);
        const storedTimestamp = localStorage.getItem(storageKeys.timestamp);

        if (storedSessionId && storedTimestamp) {
          const timestamp = parseInt(storedTimestamp, 10);
          const now = Date.now();

          // Check if session is still valid (within 24 hours)
          if (now - timestamp < SESSION_DURATION) {
            setSessionId(storedSessionId);

            return; // Use existing session
          }
          // Session expired, clear it
          localStorage.removeItem(storageKeys.sessionId);
          localStorage.removeItem(storageKeys.timestamp);
          localStorage.removeItem(storageKeys.messages);

        }

        // Create new session in background (non-blocking)
        setTimeout(async() => {
          try {
            const sessionData = await createAgentSession(agentName);
            const newSessionId = sessionData?.sessionId || Date.now().toString();
            setSessionId(newSessionId);

            // Store session info
            localStorage.setItem(storageKeys.sessionId, newSessionId);
            localStorage.setItem(storageKeys.timestamp, Date.now().toString());
          } catch (error) {
            // Suppress 401 errors during initialization - they're expected if backend isn't configured
            // Also suppress 403 errors during initialization - they'll be handled when user tries to send a message
            if (
              error?.code !== 401
              && error?.code !== 403
              && error?.message !== 'BACKEND_NOT_CONFIGURED'
              && error?.message !== 'FORBIDDEN'
            ) {
              // Session creation failed, will retry on first message
            }
            // Continue without session - will create one on first message
          }
        }, 100);
      } catch (error) {
        // Error initializing session, will retry on first message
      }
    };

    initializeSession();
  }, []);

  // Initialize messages
  useEffect(() => {
    const storageKeys = getStorageKeys();
    const storedMessages = localStorage.getItem(storageKeys.messages);
    const storedTimestamp = localStorage.getItem(storageKeys.timestamp);

    if (storedMessages && storedTimestamp) {
      const timestamp = parseInt(storedTimestamp, 10);
      const now = Date.now();

      if (now - timestamp < SESSION_DURATION) {
        try {
          const parsedMessages = JSON.parse(storedMessages);
          // Convert timestamp strings back to Date objects and update welcome message text
          const messagesWithDates = parsedMessages.map(msg => {
            const updatedMsg = {
              ...msg,
              timestamp: msg.timestamp ? new Date(msg.timestamp) : new Date()
            };
            // Update welcome message text based on current language
            if (msg.isWelcome) {
              updatedMsg.text = t('chatbot.welcomeMessage');
            }

            return updatedMsg;
          });
          setMessages(messagesWithDates);

          return;
        } catch (e) {
          // Invalid data
        }
      } else {
        localStorage.removeItem(storageKeys.messages);
      }
    }

    // Show welcome message if no messages
    if (messages.length === 0) {
      const welcomeMessage = {
        id: 1,
        text: t('chatbot.welcomeMessage'),
        isBot: true,
        timestamp: new Date(),
        isWelcome: true
      };
      setMessages([ welcomeMessage ]);
      localStorage.setItem(storageKeys.messages, JSON.stringify([ {
        ...welcomeMessage,
        timestamp: welcomeMessage.timestamp.toISOString()
      } ]));
      localStorage.setItem(storageKeys.timestamp, Date.now().toString());
    }
  }, [ i18n.language ]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [ messages ]);

  const handleQuestionSelect = async questionNumber => {
    const questions = getCommonQuestions(t);
    const question = questions.find(q => q.id === questionNumber);
    if (!question) { return; }

    const questionText = question.text;
    const userMessage = {
      id: Date.now(),
      text: questionText,
      isBot: false,
      timestamp: new Date()
    };

    const updatedMessages = [ ...messages, userMessage ];
    setMessages(updatedMessages);
    setIsTyping(true);

    try {
      const agentName = process.env.REACT_APP_BAAS_AGENT_NAME || 'sanad';
      // Add language context to ensure agent responds in the same language as the question
      const languageContext = i18n.language === 'ar'
        ? 'يرجى الإجابة باللغة العربية. '
        : 'Please respond in English. ';
      const messageWithContext = `${languageContext}${questionText}`;
      const result = await callAgent(agentName, messageWithContext, sessionId);

      const storageKeys = getStorageKeys();
      // Update session ID if we got a new one
      if (result.sessionId) {
        setSessionId(result.sessionId);
        localStorage.setItem(storageKeys.sessionId, result.sessionId);
      }

      let responseText = result.content;
      // Check if agent says it doesn't know the answer, replace with contact info
      if (isNoAnswerFound(responseText)) {
        responseText = t('chatbot.contactInfoMessage');
      }

      const botMessage = {
        id: Date.now() + 1,
        text: responseText,
        isBot: true,
        timestamp: new Date()
      };

      const finalMessages = [ ...updatedMessages, botMessage ];
      setMessages(finalMessages);
      localStorage.setItem(storageKeys.messages, JSON.stringify(finalMessages.map(msg => ({
        ...msg,
        timestamp: msg.timestamp.toISOString()
      }))));
    } catch (error) {
      const errorMessage = {
        id: Date.now() + 1,
        text: t('chatbot.contactInfoMessage'),
        isBot: true,
        timestamp: new Date()
      };
      const finalMessages = [ ...updatedMessages, errorMessage ];
      setMessages(finalMessages);
    } finally {
      setIsTyping(false);
    }
  };

  const handleSend = async() => {
    const currentInput = inputValue.trim();
    if (!currentInput) { return; }

    // Check character limit
    if (currentInput.length > MAX_MESSAGE_LENGTH) {
      message.error(t('chatbot.charLimitError'));

      return;
    }

    const userMessage = {
      id: Date.now(),
      text: currentInput,
      isBot: false,
      timestamp: new Date()
    };

    const updatedMessages = [ ...messages, userMessage ];
    setMessages(updatedMessages);
    setInputValue('');
    setIsTyping(true);

    try {
      // Handle specific questions directly without calling agent
      const lowerInput = currentInput.toLowerCase();

      // Check for SFDA or RSD website questions
      const hasRSD = lowerInput.includes('rsd');
      const hasSFDA = lowerInput.includes('sfda') || lowerInput.includes('الهيئة')
        || lowerInput.includes('الغذاء') || lowerInput.includes('الدواء');
      const hasWebsite = lowerInput.includes('website') || lowerInput.includes('موقع')
        || lowerInput.includes('رابط') || lowerInput.includes('صفحة');
      const hasHome = lowerInput.includes('home') || lowerInput.includes('رئيسية')
        || lowerInput.includes('الرئيسية');

      const isWebsiteQuestion = (
        (hasRSD && hasWebsite)
        || (hasSFDA && hasWebsite)
        || (hasRSD && hasHome)
        || (hasWebsite && (hasRSD || hasSFDA))
        || (hasHome && hasRSD)
      );

      if (isWebsiteQuestion) {
        const botResponse = {
          id: Date.now() + 1,
          text: t('chatbot.rsdWebsiteMessage'),
          isBot: true,
          timestamp: new Date()
        };
        setMessages([ ...updatedMessages, botResponse ]);
        setIsTyping(false);

        return;
      }

      // Check for "who are you" questions
      if (
        lowerInput.includes('who are you')
        || lowerInput.includes('من أنت')
        || lowerInput.includes('من انت')
        || (lowerInput.includes('what') && lowerInput.includes('you'))
        || (lowerInput.includes('ما') && lowerInput.includes('أنت'))
      ) {
        const botResponse = {
          id: Date.now() + 1,
          text: t('chatbot.agentIdentityMessage'),
          isBot: true,
          timestamp: new Date()
        };
        setMessages([ ...updatedMessages, botResponse ]);
        setIsTyping(false);

        return;
      }

      // Check for "who am I" or "how am I" questions (about the user)
      if (
        lowerInput.includes('who am i')
        || lowerInput.includes('how am i')
        || lowerInput.includes('من أنا')
        || lowerInput.includes('من انا')
        || lowerInput.includes('ما هو اسمي')
        || lowerInput.includes('ما اسمي')
        || (lowerInput.includes('my') && (lowerInput.includes('name') || lowerInput.includes('info')))
        || (lowerInput.includes('اسمي') || lowerInput.includes('معلوماتي'))
      ) {
        const userInfoMsg = getUserInfoMessage();
        const botResponse = {
          id: Date.now() + 1,
          text: userInfoMsg,
          isBot: true,
          timestamp: new Date()
        };
        setMessages([ ...updatedMessages, botResponse ]);
        setIsTyping(false);

        return;
      }

      const agentName = process.env.REACT_APP_BAAS_AGENT_NAME || 'sanad';

      // Define outputSchema for out-of-scope detection
      const outputSchema = {
        type: 'object',
        properties: {
          isSystemRelated: {
            type: 'boolean',
            description: 'Whether the question is related to the RSD system, its features, navigation, reports, or how to use the system. Questions about finding features, reports, menus, or system functionality should be marked as true.'
          },
          response: {
            type: 'string',
            description: `The answer to the question. ${i18n.language === 'ar' ? 'يرجى الإجابة باللغة العربية.' : 'Please respond in English.'} If the question is system-related, provide a helpful answer. If not system-related, you can still provide a response if you have relevant information.`
          }
        },
        required: [ 'isSystemRelated', 'response' ],
        additionalProperties: false
      };

      // Add language context to ensure agent responds in the same language as the user's message
      const languageContext = i18n.language === 'ar'
        ? 'يرجى الإجابة باللغة العربية. '
        : 'Please respond in English. ';
      const messageWithContext = `${languageContext}${currentInput}`;
      const result = await callAgent(agentName, messageWithContext, sessionId, outputSchema);

      const storageKeys = getStorageKeys();
      // Update session ID if we got a new one
      if (result.sessionId) {
        setSessionId(result.sessionId);
        localStorage.setItem(storageKeys.sessionId, result.sessionId);
      }

      // Parse JSON response if it's a string
      let parsedResult = result;
      if (typeof result.content === 'string') {
        try {
          parsedResult = JSON.parse(result.content);
        } catch (e) {
          // Not JSON, use as-is
          parsedResult = { isSystemRelated: true, response: result.content };
        }
      }

      // Handle response - check if it's out of scope
      // If isSystemRelated is false AND no useful response, show contact info
      // If there's a response, use it even if marked as not system-related
      let responseText;
      const hasResponse = parsedResult?.response && parsedResult.response.trim().length > 0;

      if (parsedResult?.isSystemRelated === false && !hasResponse) {
        // Question is out of scope and no response provided - show contact info
        responseText = t('chatbot.contactInfoMessage');
      } else if (hasResponse) {
        // There's a response - use it (even if marked as not system-related)
        responseText = parsedResult.response;
        // Check if agent says it doesn't know the answer, replace with contact info
        if (isNoAnswerFound(responseText)) {
          responseText = t('chatbot.contactInfoMessage');
        }
      } else {
        // Fallback: try to get response from content
        const fallbackText = parsedResult?.response || result?.content;
        if (fallbackText && fallbackText.trim().length > 0) {
          // Check if fallback indicates no answer found
          if (isNoAnswerFound(fallbackText)) {
            responseText = t('chatbot.contactInfoMessage');
          } else {
            responseText = fallbackText;
          }
        } else {
          // No response at all - show contact info
          responseText = t('chatbot.contactInfoMessage');
        }
      }

      const botMessage = {
        id: Date.now() + 1,
        text: responseText,
        isBot: true,
        timestamp: new Date()
      };

      const finalMessages = [ ...updatedMessages, botMessage ];
      setMessages(finalMessages);
      localStorage.setItem(storageKeys.messages, JSON.stringify(finalMessages.map(msg => ({
        ...msg,
        timestamp: msg.timestamp.toISOString()
      }))));
    } catch (error) {
      const storageKeys = getStorageKeys();
      // Check if it's a backend configuration error or forbidden error
      let errorText;
      if (error?.message === 'BACKEND_NOT_CONFIGURED' || error?.code === 401) {
        errorText = i18n.language === 'ar'
          ? 'عذراً، خدمة المساعد الذكي غير متاحة حالياً. يرجى التواصل مع الدعم الفني.'
          : 'Sorry, the AI assistant service is currently unavailable. Please contact technical support.';
      } else if (error?.message === 'FORBIDDEN' || error?.code === 403) {
        errorText = i18n.language === 'ar'
          ? 'عذراً، ليس لديك صلاحية للوصول إلى المساعد الذكي. '
            + 'يرجى التحقق من رمز الوصول أو التواصل مع الدعم الفني.'
          : 'Sorry, you do not have permission to access the AI assistant. '
            + 'Please check your access token or contact technical support.';
      } else {
        errorText = t('chatbot.contactInfoMessage');
      }

      const errorMessage = {
        id: Date.now() + 1,
        text: errorText,
        isBot: true,
        timestamp: new Date()
      };
      const finalMessages = [ ...updatedMessages, errorMessage ];
      setMessages(finalMessages);
      localStorage.setItem(storageKeys.messages, JSON.stringify(finalMessages.map(msg => ({
        ...msg,
        timestamp: msg.timestamp.toISOString()
      }))));
    } finally {
      setIsTyping(false);
    }
  };

  const handleKeyPress = e => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const formatTimestamp = timestamp => {
    if (!timestamp) { return ''; }
    const date = timestamp instanceof Date ? timestamp : new Date(timestamp);
    if (isNaN(date.getTime())) { return ''; }

    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  // Check if welcome message should be shown (only when starting new session or after clearing)
  const showWelcomeAndQuestions = messages.length === 1 && messages[0]?.isWelcome;

  return (
    <LabeledCard
      title={ t('pageTitles.chatbot') }
      icon={
        <img
          src={ robotIconBlack }
          alt="Robot"
          style={{ width: '32px', height: '32px' }}
        />
      }
      className={ style.chatbotCard }
    >
      <div className={ style.chatContainer }>
        <div className={ style.messagesContainer }>
          {messages.map(msg => (
            <div
              key={ msg.id }
              className={ `${style.message} ${msg.isBot ? style.botMessage : style.userMessage}` }
              style={{
                gap: i18n.language === 'ar' ? '20px' : '12px'
              }}
            >
              <Avatar
                size="small"
                icon={
                  msg.isBot
                    ? (
                      <img
                        src={ robotIconWhite }
                        alt="Robot"
                        style={{
                          width: '18px',
                          height: '18px',
                          objectFit: 'contain',
                          display: 'block',
                          margin: '0 auto'
                        }}
                      />
                    )
                    : <UserOutlined />
                }
                className={ msg.isBot ? style.botAvatar : style.userAvatar }
                style={{ alignSelf: 'flex-end' }}
              />
              <div className={ style.messageContent }>
                <div className={ style.messageText }>{formatMessageText(msg.text)}</div>
                <div
                  className={ style.messageTime }
                  style={{
                    textAlign: (() => {
                      if (i18n.language === 'ar') {
                        return msg.isBot ? 'right' : 'left';
                      }

                      return msg.isBot ? 'left' : 'right';
                    })()
                  }}
                >
                  {formatTimestamp(msg.timestamp)}
                </div>
              </div>
            </div>
          ))}
          {isTyping && (
            <div className={ `${style.message} ${style.botMessage}` }>
              <Avatar
                size="small"
                icon={
                  <img
                    src={ robotIconWhite }
                    alt="Robot"
                    style={{
                      width: '18px',
                      height: '18px',
                      objectFit: 'contain',
                      display: 'block',
                      margin: '0 auto'
                    }}
                  />
                }
                className={ style.botAvatar }
                style={{ alignSelf: 'flex-end' }}
              />
              <div className={ style.messageContent }>
                <div className={ style.typingIndicator }>
                  <span />
                  <span />
                  <span />
                </div>
              </div>
            </div>
          )}

          {/* Show common questions after welcome message */}
          {showWelcomeAndQuestions && (
            <div className={ `${style.message} ${style.botMessage}` }>
              <Avatar
                size="small"
                icon={
                  <img
                    src={ robotIconWhite }
                    alt="Robot"
                    style={{
                      width: '18px',
                      height: '18px',
                      objectFit: 'contain',
                      display: 'block',
                      margin: '0 auto'
                    }}
                  />
                }
                className={ style.botAvatar }
                style={{ alignSelf: 'flex-end' }}
              />
              <div className={ style.messageContent }>
                <div className={ style.messageText }>
                  <div style={{ marginBottom: '12px', fontWeight: 'bold', fontSize: '14px', color: '#1890ff' }}>
                    {i18n.language === 'ar' ? 'الأسئلة الشائعة:' : 'Common Questions:'}
                  </div>
                  {getCommonQuestions(t).map((q, index, questions) => (
                    <div
                      key={ q.id }
                      style={{
                        cursor: 'pointer',
                        padding: '8px 0',
                        borderBottom: index < questions.length - 1 ? '1px solid #e6f7ff' : 'none',
                        transition: 'background-color 0.2s'
                      }}
                      onClick={ () => handleQuestionSelect(q.id) }
                      onKeyDown={ e => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          handleQuestionSelect(q.id);
                        }
                      } }
                      onMouseEnter={ e => {
                        e.currentTarget.style.backgroundColor = '#e6f7ff';
                      } }
                      onMouseLeave={ e => {
                        e.currentTarget.style.backgroundColor = 'transparent';
                      } }
                      role="button"
                      tabIndex={ 0 }
                    >
                      {q.id}
                      .
                      {' '}
                      {q.text}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          <div ref={ messagesEndRef } />
        </div>

        <div className={ style.inputContainer }>
          <textarea
            ref={ textareaRef }
            value={ inputValue }
            onChange={ e => {
              const value = e.target.value;
              if (value.length <= MAX_MESSAGE_LENGTH) {
                setInputValue(value);
                adjustTextareaHeight();
              } else {
                message.error(t('chatbot.charLimitError'));
              }
            } }
            onKeyPress={ handleKeyPress }
            placeholder={ t('typeMessage') || (i18n.language === 'ar' ? 'اكتب رسالتك...' : 'Type your message...') }
            className={ style.messageInput }
            maxLength={ MAX_MESSAGE_LENGTH }
            style={{
              width: '100%',
              minHeight: '32px',
              maxHeight: '96px',
              padding: '4px 11px',
              border: '1px solid #d9d9d9',
              borderRadius: '4px',
              resize: 'none',
              overflow: 'auto',
              fontFamily: 'inherit',
              fontSize: '14px',
              lineHeight: '1.5'
            }}
          />
          <Button
            onClick={ handleSend }
            disabled={ !inputValue.trim() }
            style={{
              backgroundColor: inputValue.trim() ? '#007bff' : '#6c757d',
              color: 'white',
              border: 'none',
              borderRadius: '50%',
              width: '40px',
              height: '40px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: inputValue.trim() ? 'pointer' : 'not-allowed'
            }}
          >
            <SendSvg height={ 16 } width={ 16 } />
          </Button>
        </div>
      </div>
    </LabeledCard>
  );
};
